/**
 * 统一的注释管理系统
 * 负责从后端获取、缓存和管理游戏参数的注释信息
 */

export interface ParameterComment {
  id: number;
  gameTypeId: number;
  configFile: string;
  configSection: string;
  parameterKey: string;
  parameterComment: string;
  parameterDescription: string;
  createdAt: string;
  updatedAt: string;
}

export interface CommentCache {
  [key: string]: {
    comment: string;
    description: string;
  };
}

class CommentManager {
  private cache: CommentCache = {};
  private gameTypeId: number | null = null;
  private isLoading = false;
  private loadPromise: Promise<void> | null = null;

  /**
   * 初始化注释管理器
   * @param gameTypeId 游戏类型ID
   */
  async initialize(gameTypeId: number): Promise<void> {
    if (this.gameTypeId === gameTypeId && Object.keys(this.cache).length > 0) {
      return; // 已经初始化过了
    }

    this.gameTypeId = gameTypeId;
    await this.loadComments();
  }

  /**
   * 加载注释数据
   */
  private async loadComments(): Promise<void> {
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    this.isLoading = true;
    this.loadPromise = this.fetchCommentsFromAPI();
    
    try {
      await this.loadPromise;
    } finally {
      this.isLoading = false;
      this.loadPromise = null;
    }
  }

  /**
   * 从API获取注释数据
   */
  private async fetchCommentsFromAPI(): Promise<void> {
    try {
      const response = await fetch(`/api/ark/parameter-comments/${this.gameTypeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load comments: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        // API返回的是已经格式化的注释映射表
        this.cache = result.data;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Failed to load parameter comments:', error);
      this.cache = {}; // 清空缓存，避免使用过期数据
    }
  }

  // buildCache方法已移除，因为API直接返回格式化的数据

  /**
   * 生成注释查找键
   */
  private generateCommentKey(configFile: string, configSection: string, parameterKey: string): string {
    return `${configFile}_${configSection}_${parameterKey}`;
  }

  /**
   * 查找参数注释
   * @param configFile 配置文件名称 (如: GameUserSettings, GameIni)
   * @param configSection 配置分组 (如: ServerSettings, /Script/ShooterGame.ShooterGameMode)
   * @param parameterKey 参数键名
   * @returns 注释信息，如果未找到返回null
   */
  findComment(configFile: string, configSection: string, parameterKey: string): { comment: string; description: string } | null {
    // 尝试多种键格式
    const possibleKeys = [
      this.generateCommentKey(configFile, configSection, parameterKey),
      this.generateCommentKey(configFile, 'ServerSettings', parameterKey),
      this.generateCommentKey(configFile, '/Script/ShooterGame.ShooterGameUserSettings', parameterKey),
      this.generateCommentKey(configFile, '/Script/Engine.HUD', parameterKey),
      this.generateCommentKey(configFile, '/Script/EngineSettings.GeneralProjectSettings', parameterKey),
      this.generateCommentKey(configFile, '/Script/ShooterGame.ShooterGameMode', parameterKey),
      this.generateCommentKey(configFile, '/Script/Engine.GameNetworkManager', parameterKey),
      this.generateCommentKey(configFile, '/Script/Engine.GameSession', parameterKey)
    ];

    for (const key of possibleKeys) {
      if (this.cache[key]) {
        return this.cache[key];
      }
    }

    return null;
  }

  /**
   * 批量查找参数注释
   * @param parameters 参数列表，格式: [{ configFile, configSection, parameterKey }]
   * @returns 注释映射表
   */
  findCommentsBatch(parameters: Array<{ configFile: string; configSection: string; parameterKey: string }>): { [key: string]: { comment: string; description: string } } {
    const result: { [key: string]: { comment: string; description: string } } = {};

    for (const param of parameters) {
      const comment = this.findComment(param.configFile, param.configSection, param.parameterKey);
      if (comment) {
        const key = `${param.configFile}_${param.configSection}_${param.parameterKey}`;
        result[key] = comment;
      }
    }

    return result;
  }

  /**
   * 获取所有注释数据
   */
  getAllComments(): CommentCache {
    return { ...this.cache };
  }

  /**
   * 清空缓存
   */
  clearCache(): void {
    this.cache = {};
    this.gameTypeId = null;
  }

  /**
   * 检查是否已初始化
   */
  isInitialized(): boolean {
    return this.gameTypeId !== null && Object.keys(this.cache).length > 0;
  }

  /**
   * 获取缓存统计信息
   */
  getCacheStats(): { totalComments: number; gameTypeId: number | null } {
    return {
      totalComments: Object.keys(this.cache).length,
      gameTypeId: this.gameTypeId
    };
  }
}

// 创建全局单例实例
export const commentManager = new CommentManager();

// 导出类型和实例
export default commentManager;
